
const libPictProvider = require('pict-provider');
const SpecsCatalog = require('./CustomerProvisioning-SpecsCatalog.js');
const PersonaCatalog = require('./CustomerProvisioning-PersonaCatalog.js');
const RoleCatalog = require('./CustomerProvisioning-RoleCatalog.js');

class CustomerProvisioningProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/**
	 * Trigger a declarative repaint of the view from current AppData.
	 */
	render()
	{
		this.pict.views.CustomerProvisioning.render();
	}

	generatePassword()
	{
		const tmpWords = [
			'anchor','asphalt','beacon','bridge','canyon','cement','cobalt','crane',
			'delta','diesel','ember','falcon','gauge','gravel','harbor','iron',
			'jetty','keystone','lantern','marble','nimbus','orbit','paving','quarry',
			'rebar','signal','timber','truss','upland','valley','welded','zenith',
			'alpine','barrel','conduit','dredge','flange','girder','hopper','joist',
			'kiln','ledger','mallet','nozzle','outlet','plinth','rivet','spanner',
			'toggle','umbra','vessel','wrench','axle','bolted','clamp','dowel'
		];
		let tmpWord1 = tmpWords[Math.floor(Math.random() * tmpWords.length)];
		let tmpWord2 = tmpWords[Math.floor(Math.random() * tmpWords.length)];
		// Capitalize first letter of each
		tmpWord1 = tmpWord1.charAt(0).toUpperCase() + tmpWord1.slice(1);
		tmpWord2 = tmpWord2.charAt(0).toUpperCase() + tmpWord2.slice(1);
		return `${tmpWord1}-${tmpWord2}`;
	}

	// --- State Initialization ---

	initializeState()
	{
		this.pict.AppData.CustomerProvisioning = {
			ActiveStep: 1,
			CompletedSteps: [],
			SelectedDataset: null,
			DatasetFilter: '',
			DatasetRows: [],
			DatasetSummarySlot: [],
			PersonaGroups: [],
			RoleRows: [],
			CustomerLabel: '',
			AdminLabel: '',
			PersonaSelectedCount: 0,
			DatasetLabel: 'None',
			SpecRecords: 0,
			RoleCount: 0,
			Customer: {
				Name: '',
				Code: '',
				Timezone: 'America/New_York',
				GUIDCustomer: '',
			},
			AdminUser: {
				NameFirst: '',
				NameLast: '',
				Email: '',
				LoginID: '',
				LoginPassword: '',
			},
			IntegrationUser: {
				NameFirst: 'Integration',
				NameLast: 'Service',
				Email: '',
				LoginID: '',
				LoginPassword: 'HeadLight2026!',
			},
			ExampleUsers: PersonaCatalog.map((pPersona) =>
			{
				return {
					selected: true,
					persona: pPersona,
					userData: {
						NameFirst: pPersona.defaultName.first,
						NameLast: pPersona.defaultName.last,
						Email: '',
						LoginID: '',
						Title: pPersona.title,
					},
				};
			}),
			Comprehension1: null,
			Comprehension2: null,
			PushState: {
				Phase: 'idle',
				Progress: 0,
				StatusText: '',
				PanelClass: 'is-hidden',
				TotalSteps: 0,
				CurrentEntity: '',
				Errors: [],
				Log: [],
			},
		};
	}

	// --- Derived Render State ---

	/**
	 * Compute the per-step accordion body visibility + status tag slots from
	 * ActiveStep / CompletedSteps. Uses Bulma is-hidden (not inline display).
	 */
	deriveStepState()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		for (let i = 1; i <= 5; i++)
		{
			tmpState['Step' + i + '_BodyClass'] = (tmpState.ActiveStep === i) ? '' : 'is-hidden';

			if (tmpState.CompletedSteps.includes(i))
			{
				tmpState['Step' + i + '_StatusLabel'] = '✓ Complete';
				tmpState['Step' + i + '_StatusClass'] = 'is-success';
			}
			else if (tmpState.ActiveStep === i)
			{
				tmpState['Step' + i + '_StatusLabel'] = 'Active';
				tmpState['Step' + i + '_StatusClass'] = 'is-info';
			}
			else
			{
				tmpState['Step' + i + '_StatusLabel'] = 'Pending';
				tmpState['Step' + i + '_StatusClass'] = '';
			}
		}
	}

	/**
	 * Build the dataset grid rows from the specs catalog, applying the active
	 * filter (is-hidden) and selected-row highlight.
	 */
	deriveDatasetRows()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		let tmpFilter = (tmpState.DatasetFilter || '').toLowerCase();
		let tmpSelectedCode = tmpState.SelectedDataset ? tmpState.SelectedDataset.code : null;
		let tmpRows = [];

		let tmpSortedCodes = Object.keys(SpecsCatalog).sort((a, b) => SpecsCatalog[a].name.localeCompare(SpecsCatalog[b].name));

		for (let tmpCode of tmpSortedCodes)
		{
			let tmpDataset = SpecsCatalog[tmpCode];
			if (!tmpDataset.comprehensionRecords)
			{
				continue;
			}

			let tmpNameLower = tmpDataset.name.toLowerCase();
			let tmpCodeLower = tmpCode.toLowerCase();
			let tmpVisible = (!tmpFilter || tmpCodeLower.includes(tmpFilter) || tmpNameLower.includes(tmpFilter));

			let tmpRowClass = tmpVisible ? '' : 'is-hidden';
			if (tmpSelectedCode && tmpDataset.code === tmpSelectedCode)
			{
				tmpRowClass = (tmpRowClass + ' has-background-info-light').trim();
			}

			tmpRows.push({
				Code: tmpDataset.code,
				Name: tmpDataset.name,
				Records: tmpDataset.comprehensionRecords.toLocaleString(),
				TestForms: tmpDataset.testForms || 0,
				TestProcedures: tmpDataset.testProcedures || 0,
				ExampleProjects: tmpDataset.exampleProjects || 0,
				BidTabs: tmpDataset.bidTabFiles || 0,
				NameLower: tmpNameLower,
				RowClass: tmpRowClass,
			});
		}

		tmpState.DatasetRows = tmpRows;
	}

	/**
	 * Build the persona checkbox groups (Field / Project Office / Back Office).
	 */
	derivePersonaGroups()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		let tmpCategories = ['Field', 'Project Office', 'Back Office'];
		let tmpGroups = [];

		for (let tmpCatIdx = 0; tmpCatIdx < tmpCategories.length; tmpCatIdx++)
		{
			let tmpCategory = tmpCategories[tmpCatIdx];
			let tmpUsers = [];

			for (let i = 0; i < tmpState.ExampleUsers.length; i++)
			{
				let tmpEU = tmpState.ExampleUsers[i];
				if (tmpEU.persona.category !== tmpCategory)
				{
					continue;
				}

				tmpUsers.push({
					Index: i,
					NameFirst: tmpEU.userData.NameFirst,
					NameLast: tmpEU.userData.NameLast,
					Title: tmpEU.persona.title,
					CheckedAttr: tmpEU.selected ? 'checked' : '',
				});
			}

			tmpGroups.push({ Category: tmpCategory, Users: tmpUsers });
		}

		tmpState.PersonaGroups = tmpGroups;
	}

	/**
	 * Build the role summary rows from the role catalog.
	 */
	deriveRoleRows()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		let tmpRows = [];

		for (let tmpKey in RoleCatalog)
		{
			let tmpRole = RoleCatalog[tmpKey];
			tmpRows.push({
				Name: tmpRole.Name || tmpKey,
				Code: tmpRole.Code || tmpKey,
				Type: tmpRole.Type || 'Role',
			});
		}

		tmpState.RoleRows = tmpRows;
	}

	/**
	 * Compute the scalar review-summary fields for Step 5.
	 */
	deriveReviewState()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		let tmpSelectedCount = tmpState.ExampleUsers.filter((pEU) => pEU.selected).length;

		tmpState.CustomerLabel = `${tmpState.Customer.Name} (${tmpState.Customer.Code})`;
		tmpState.AdminLabel = `${tmpState.AdminUser.NameFirst} ${tmpState.AdminUser.NameLast} <${tmpState.AdminUser.Email}>`;
		tmpState.PersonaSelectedCount = tmpSelectedCount;
		tmpState.DatasetLabel = tmpState.SelectedDataset ? tmpState.SelectedDataset.name : 'None';
		tmpState.SpecRecords = tmpState.SelectedDataset ? tmpState.SelectedDataset.comprehensionRecords : 0;
		tmpState.RoleCount = Object.keys(RoleCatalog).length;
	}

	/**
	 * Derive the push-progress panel visibility from PushState.Phase.
	 */
	derivePushState()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		tmpState.PushState.PanelClass = (tmpState.PushState.Phase !== 'idle') ? '' : 'is-hidden';
	}

	// --- Accordion Navigation ---

	goToStep(pStepNumber)
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		// Can only go to completed steps or the next uncompleted step
		if (pStepNumber > 1 && !tmpState.CompletedSteps.includes(pStepNumber - 1) && tmpState.ActiveStep !== pStepNumber)
		{
			return;
		}

		tmpState.ActiveStep = pStepNumber;
		this.render();
	}

	completeStep(pStepNumber)
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		if (!tmpState.CompletedSteps.includes(pStepNumber))
		{
			tmpState.CompletedSteps.push(pStepNumber);
		}

		this.goToStep(pStepNumber + 1);
	}

	// --- Step 1: Dataset Selection ---

	filterDatasetTable(pFilter)
	{
		this.pict.AppData.CustomerProvisioning.DatasetFilter = pFilter || '';
		this.deriveDatasetRows();
		// Repaint ONLY the table rows, not the whole view — a full render() would rebuild the filter
		// <input> (a sibling of #CP-Dataset-Rows) and drop focus/caret after each keystroke.
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || tmpCA.getElement('#CP-Dataset-Rows').length === 0) { this.render(); return; }
		tmpCA.assignContent('#CP-Dataset-Rows', this.pict.parseTemplateSetByHash('CustomerProvisioning-DatasetRow', this.pict.AppData.CustomerProvisioning.DatasetRows));
	}

	selectDataset(pCode)
	{
		let tmpDataset = SpecsCatalog[pCode];
		if (!tmpDataset)
		{
			return;
		}

		let tmpState = this.pict.AppData.CustomerProvisioning;
		tmpState.SelectedDataset = tmpDataset;

		// Re-derive so the selected row highlights
		this.deriveDatasetRows();

		// Single-element array drives the summary notification template
		tmpState.DatasetSummarySlot = [{
			Name: tmpDataset.name,
			Code: tmpDataset.code,
			Records: tmpDataset.comprehensionRecords,
			ExampleProjects: tmpDataset.exampleProjects,
		}];

		this.render();
	}

	// --- Step 2: Customer Configuration ---

	saveCustomerConfig()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		tmpState.Customer.Name = (this.pict.ContentAssignment.readContent('#CP-CustomerName') || '').trim();
		tmpState.Customer.Code = (this.pict.ContentAssignment.readContent('#CP-CustomerCode') || '').trim();
		tmpState.Customer.Timezone = this.pict.ContentAssignment.readContent('#CP-Timezone') || 'America/New_York';

		if (!tmpState.Customer.Name || !tmpState.Customer.Code)
		{
			this.pict.views.HeadlightPanels?.toast({ message: 'Please fill in Customer Name and Code', type: 'is-warning' });
			return;
		}

		tmpState.Customer.GUIDCustomer = 'CUST-' + tmpState.Customer.Code;

		// Generate a shared password for all accounts
		let tmpPassword = this.generatePassword();
		tmpState._generatedPassword = tmpPassword;

		// Auto-populate admin user
		tmpState.AdminUser.NameFirst = tmpState.Customer.Code;
		tmpState.AdminUser.NameLast = 'Admin';
		tmpState.AdminUser.Email = `developer+${tmpState.Customer.Code}-Admin@headlight.com`;
		tmpState.AdminUser.LoginID = `${tmpState.Customer.Code}-admin`;
		tmpState.AdminUser.LoginPassword = tmpPassword;

		// Auto-generate integration user
		tmpState.IntegrationUser.LoginID = tmpState.Customer.Code + '-integration';
		tmpState.IntegrationUser.Email = `developer+${tmpState.Customer.Code}-Integration@headlight.com`;
		tmpState.IntegrationUser.LoginPassword = tmpPassword;

		// Generate example user emails and passwords based on customer code
		for (let tmpIdx = 0; tmpIdx < tmpState.ExampleUsers.length; tmpIdx++)
		{
			let tmpEU = tmpState.ExampleUsers[tmpIdx];
			tmpEU.userData.Email = `developer+${tmpState.Customer.Code}-${tmpEU.persona.defaultName.first}@headlight.com`;
			tmpEU.userData.LoginID = `${tmpEU.persona.defaultName.first.toLowerCase()}.${tmpEU.persona.defaultName.last.toLowerCase()}`;
			tmpEU.userData.LoginPassword = tmpPassword;
		}

		// Step-3 inputs are bound to AppData, so they show the auto-populated
		// values after the re-render triggered by completeStep().
		this.completeStep(2);
	}

	// --- Step 3: User Configuration ---

	saveUserConfig()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		tmpState.AdminUser.NameFirst = (this.pict.ContentAssignment.readContent('#CP-AdminFirst') || '').trim();
		tmpState.AdminUser.NameLast = (this.pict.ContentAssignment.readContent('#CP-AdminLast') || '').trim();
		tmpState.AdminUser.Email = (this.pict.ContentAssignment.readContent('#CP-AdminEmail') || '').trim();
		tmpState.AdminUser.LoginID = (this.pict.ContentAssignment.readContent('#CP-AdminLogin') || '').trim();
		tmpState.AdminUser.LoginPassword = this.pict.ContentAssignment.readContent('#CP-AdminPassword') || '';
		tmpState.IntegrationUser.LoginPassword = this.pict.ContentAssignment.readContent('#CP-IntegrationPassword') || 'HeadLight2026!';

		if (!tmpState.AdminUser.NameFirst || !tmpState.AdminUser.Email || !tmpState.AdminUser.LoginID || !tmpState.AdminUser.LoginPassword)
		{
			this.pict.views.HeadlightPanels?.toast({ message: 'Please fill in all admin user fields', type: 'is-warning' });
			return;
		}

		this.completeStep(3);
	}

	togglePersona(pIndex)
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		tmpState.ExampleUsers[pIndex].selected = !tmpState.ExampleUsers[pIndex].selected;
		this.derivePersonaGroups();
		this.render();
	}

	// --- Step 4: Security / Roles ---

	saveSecurityConfig()
	{
		this.completeStep(4);
	}

	// --- Comprehension Building ---

	buildComprehensions()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		// Every provisioned GUID is SCOPED to this customer's Code (there is only a GUID convention, no auto-scoping),
		// so approving a SECOND customer never collides with (or clobbers) the first's PROV-USER-admin / PROV-<role>
		// records. Child records carry GUIDCustomer so the server resolves them onto the NEW customer (CUST-<Code>,
		// created first). Permissions are FLATTENED directly onto each role (its child permission groups' hashes) via
		// RolePermission rather than role→permission-group RoleEdges — RoleEdge's parent/child FKs are not
		// GUID-resolvable in a comprehension, and a flattened role grants the same effective access.
		const tmpCode = String(tmpState.Customer.Code || '').trim();
		const tmpSfx = '-' + tmpCode;
		const tmpCustomerGUID = 'CUST-' + tmpCode;

		// --- Comprehension 1: Customer, Users, Roles, admin/integration UserRoleJoins ---
		let tmpComprehension1 = {};

		// Customer (created first — its GUID→ID mapping is what scopes the child records below)
		tmpComprehension1.Customer = {};
		tmpComprehension1.Customer[tmpCustomerGUID] = {
			GUIDCustomer: tmpCustomerGUID,
			Name: tmpState.Customer.Name,
			Code: tmpState.Customer.Code,
			Enabled: true,
			Timezone: tmpState.Customer.Timezone,
		};

		// Users (admin + integration) — GUIDs scoped by Code; GUIDCustomer ties them to the new customer
		tmpComprehension1.User = {};
		const tmpAdminGUID = 'PROV-USER-admin' + tmpSfx;
		const tmpIntegrationGUID = 'PROV-USER-integration' + tmpSfx;
		tmpComprehension1.User[tmpAdminGUID] = {
			GUIDUser: tmpAdminGUID,
			GUIDCustomer: tmpCustomerGUID,
			NameFirst: tmpState.AdminUser.NameFirst,
			NameLast: tmpState.AdminUser.NameLast,
			Email: tmpState.AdminUser.Email,
			LoginID: tmpState.AdminUser.LoginID,
			LoginPassword: tmpState.AdminUser.LoginPassword,
			Title: 'Administrator',
		};
		tmpComprehension1.User[tmpIntegrationGUID] = {
			GUIDUser: tmpIntegrationGUID,
			GUIDCustomer: tmpCustomerGUID,
			NameFirst: tmpState.IntegrationUser.NameFirst,
			NameLast: tmpState.IntegrationUser.NameLast,
			Email: tmpState.IntegrationUser.Email,
			LoginID: tmpState.IntegrationUser.LoginID,
			LoginPassword: tmpState.IntegrationUser.LoginPassword,
			Title: 'Integration Service',
		};

		// Roles — GUIDs scoped by Code; GUIDCustomer ties them to the new customer.
		tmpComprehension1.Role = {};
		let tmpAllRoleEntries = [].concat(RoleCatalog.roles || [], RoleCatalog.permissionGroups || []);
		for (let tmpRoleIdx = 0; tmpRoleIdx < tmpAllRoleEntries.length; tmpRoleIdx++)
		{
			let tmpRole = tmpAllRoleEntries[tmpRoleIdx];
			let tmpRoleGUID = 'PROV-' + tmpRole.Key + tmpSfx;
			tmpComprehension1.Role[tmpRoleGUID] = {
				GUIDRole: tmpRoleGUID,
				GUIDCustomer: tmpCustomerGUID,
				Name: tmpRole.Name,
				Type: tmpRole.Type || 'Role',
				PermissionLevel: tmpRole.PermissionLevel || '',
				Deleted: 0,
			};
		}

		// Permissions: flatten each ROLE's effective permission hashes directly onto it (union of its child permission
		// groups' PermissionHashes from the catalog), scoped by Code + GUIDCustomer. GUIDRole is a standard FK the
		// server resolves; this avoids RoleEdge (whose parent/child FKs are not GUID-resolvable) while granting the
		// same access. The permission-group entries created above stay as empty placeholder roles.
		tmpComprehension1.RolePermission = {};
		let tmpPGByKey = {};
		(RoleCatalog.permissionGroups || []).forEach((pPG) => { tmpPGByKey[pPG.Key] = pPG; });
		(RoleCatalog.roles || []).forEach((pRole) =>
		{
			let tmpRoleGUID = 'PROV-' + pRole.Key + tmpSfx;
			let tmpSeen = {};
			let tmpPermIdx = 0;
			(pRole.ChildRoles || []).forEach((pChildKey) =>
			{
				let tmpPG = tmpPGByKey[pChildKey];
				if (!tmpPG || !tmpPG.PermissionHashes) { return; }
				Object.keys(tmpPG.PermissionHashes).forEach((pContext) =>
				{
					(tmpPG.PermissionHashes[pContext] || []).forEach((pHash) =>
					{
						let tmpDedup = pContext + '|' + pHash;
						if (tmpSeen[tmpDedup]) { return; }
						tmpSeen[tmpDedup] = true;
						let tmpRPGUID = 'PROV-RP-' + pRole.Key + '-' + (tmpPermIdx++) + tmpSfx;
						tmpComprehension1.RolePermission[tmpRPGUID] = {
							GUIDRolePermission: tmpRPGUID,
							GUIDCustomer: tmpCustomerGUID,
							GUIDRole: tmpRoleGUID,
							Hash: pHash,
							Allowed: 1,
							Context: pContext,
						};
					});
				});
			});
		});

		// Membership: link the admin + integration users into the HeadLight-admin role (standard GUIDUser/GUIDRole FKs).
		tmpComprehension1.UserRoleJoin = {};
		const tmpAdminRoleGUID = 'PROV-UM-STD-R-HL-ADMIN' + tmpSfx;
		tmpComprehension1.UserRoleJoin['PROV-URJ-admin' + tmpSfx] = {
			GUIDUserRoleJoin: 'PROV-URJ-admin' + tmpSfx,
			GUIDCustomer: tmpCustomerGUID,
			GUIDUser: tmpAdminGUID,
			GUIDRole: tmpAdminRoleGUID,
		};
		tmpComprehension1.UserRoleJoin['PROV-URJ-integration' + tmpSfx] = {
			GUIDUserRoleJoin: 'PROV-URJ-integration' + tmpSfx,
			GUIDCustomer: tmpCustomerGUID,
			GUIDUser: tmpIntegrationGUID,
			GUIDRole: tmpAdminRoleGUID,
		};

		tmpState.Comprehension1 = tmpComprehension1;

		// --- Comprehension 2: Example Users (GUIDs scoped by Code; tied to the new customer) ---
		let tmpComprehension2 = {};
		tmpComprehension2.User = {};
		tmpComprehension2.UserRoleJoin = {};

		let tmpSelectedUsers = tmpState.ExampleUsers.filter((pEU) => pEU.selected);
		for (let tmpIdx = 0; tmpIdx < tmpSelectedUsers.length; tmpIdx++)
		{
			let tmpEU = tmpSelectedUsers[tmpIdx];
			let tmpUserGUID = 'PROV-USER-example-' + tmpIdx + tmpSfx;
			tmpComprehension2.User[tmpUserGUID] = {
				GUIDUser: tmpUserGUID,
				GUIDCustomer: tmpCustomerGUID,
				NameFirst: tmpEU.userData.NameFirst,
				NameLast: tmpEU.userData.NameLast,
				Email: tmpEU.userData.Email,
				LoginID: tmpEU.userData.LoginID,
				Title: tmpEU.userData.Title,
			};

			// Membership: link the persona's role, but only when it names one of the catalog roles above (so the
			// scoped GUIDRole actually resolves). Persona role key lives on `.role` (the old `.roleCode` was always
			// undefined, so no example memberships were ever created).
			const tmpPersonaRole = tmpEU.persona && (tmpEU.persona.role || tmpEU.persona.roleCode);
			const tmpPersonaRoleGUID = tmpPersonaRole ? ('PROV-' + tmpPersonaRole + tmpSfx) : '';
			if (tmpPersonaRoleGUID && tmpComprehension1.Role[tmpPersonaRoleGUID])
			{
				let tmpURJGUID = 'PROV-URJ-example-' + tmpIdx + tmpSfx;
				tmpComprehension2.UserRoleJoin[tmpURJGUID] = {
					GUIDUserRoleJoin: tmpURJGUID,
					GUIDCustomer: tmpCustomerGUID,
					GUIDUser: tmpUserGUID,
					GUIDRole: tmpPersonaRoleGUID,
				};
			}
		}

		tmpState.Comprehension2 = tmpComprehension2;
	}

	// --- Download ---

	downloadComprehension(pPhase)
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		if (!tmpState.Comprehension1)
		{
			this.buildComprehensions();
		}

		let tmpData = (pPhase === 1) ? tmpState.Comprehension1 : tmpState.Comprehension2;
		let tmpJSON = JSON.stringify(tmpData, null, 2);
		let tmpBlob = new Blob([tmpJSON], { type: 'application/json' });
		let tmpURL = URL.createObjectURL(tmpBlob);

		// Intentional exception: the anchor-download idiom (createElement('a') +
		// click) is the only browser way to trigger a file download; it has no
		// Pict/template equivalent. This is the sole permitted document.* usage here.
		let tmpLink = document.createElement('a');
		tmpLink.href = tmpURL;
		tmpLink.download = `${tmpState.Customer.Code}_phase${pPhase}_comprehension.json`;
		document.body.appendChild(tmpLink);
		tmpLink.click();
		document.body.removeChild(tmpLink);
		URL.revokeObjectURL(tmpURL);
	}

	// --- Submit as a New Customer request Document (provisions on approval) ---

	/**
	 * Merge the two GUID-keyed comprehension maps ({ Entity: { GUID: record } }) that buildComprehensions() produces
	 * into one, dropping empty buckets. This is exactly the shape the server's OnEnteringState comprehension applier
	 * (the IntegrationAdapter) consumes — so the request document's onApprove payload is the wizard's own output.
	 */
	_mergeComprehensions(pC1, pC2)
	{
		const tmpOut = {};
		[ pC1, pC2 ].forEach((pC) =>
		{
			if (!pC || (typeof pC !== 'object')) { return; }
			Object.keys(pC).forEach((pEntity) =>
			{
				const tmpBucket = pC[pEntity];
				if (!tmpBucket || (typeof tmpBucket !== 'object')) { return; }
				const tmpKeys = Object.keys(tmpBucket);
				if (!tmpKeys.length) { return; }
				if (!tmpOut[pEntity]) { tmpOut[pEntity] = {}; }
				tmpKeys.forEach((pGUID) => { tmpOut[pEntity][pGUID] = tmpBucket[pGUID]; });
			});
		});
		return tmpOut;
	}

	/** The request form snapshot stored on the Document (addresses match the HeadLight-NewCustomer RNI form). */
	_requestFormData(pState)
	{
		return {
			Customer: { Name: pState.Customer.Name, Code: pState.Customer.Code, Timezone: pState.Customer.Timezone },
			AdminUser: { NameFirst: pState.AdminUser.NameFirst, NameLast: pState.AdminUser.NameLast, Email: pState.AdminUser.Email, LoginID: pState.AdminUser.LoginID },
			DatasetLabel: pState.DatasetLabel || (pState.SelectedDataset && pState.SelectedDataset.name) || '',
		};
	}

	/** Find (or create) the HeadLight "New Customers" project that holds the request documents. Idempotent. */
	async _ensureNewCustomersProjectID()
	{
		if (this._newCustomersProjectID) { return this._newCustomersProjectID; }
		const tmpFound = await new Promise((pResolve) => this.restClient.getJSON('Projects/FilteredTo/FBV~Name~EQ~New Customers~FBN~IDCustomer~EQ~1~FBV~Deleted~EQ~0/0/1', (pErr, pBody) => pResolve(pErr ? null : pBody)));
		if (Array.isArray(tmpFound) && tmpFound[0] && tmpFound[0].IDProject) { this._newCustomersProjectID = tmpFound[0].IDProject; return this._newCustomersProjectID; }
		const tmpCreated = await this.upsertRecord('Project', { IDCustomer: 1, Name: 'New Customers', Description: 'Platform project holding New Customer provisioning requests (documents) and their observations.', Status: 'Active', Deleted: 0, InitialContractAmount: 0, GUIDProject: 'HL-NEWCUSTOMERS-PROJECT' });
		this._newCustomersProjectID = tmpCreated && tmpCreated.IDProject;
		return this._newCustomersProjectID;
	}

	/**
	 * Submit the wizard's input as a New Customer REQUEST: a Draft Document (type HeadLight-NewCustomer) in the New
	 * Customers project whose EntityData.OnEnteringState.Approved carries the provisioning comprehension. The customer
	 * (+ users, roles) is created when an approver approves the request — not here. Reuses buildComprehensions() as-is.
	 */
	async createRequestDocument()
	{
		const tmpState = this.pict.AppData.CustomerProvisioning;
		if (!tmpState || !tmpState.Customer || !tmpState.Customer.Name || !tmpState.Customer.Code)
		{
			this.pict.views.HeadlightPanels?.toast({ message: 'Please complete the customer name and code first.', type: 'is-warning' });
			return;
		}
		try
		{
			this.buildComprehensions();   // reuse the existing builder verbatim
			const tmpComprehension = this._mergeComprehensions(tmpState.Comprehension1, tmpState.Comprehension2);
			const tmpProjectID = await this._ensureNewCustomersProjectID();
			const tmpDoc = {
				IDCustomer: 1,
				IDProject: tmpProjectID,
				DocumentType: 'HeadLight-NewCustomer',
				Form: 1,
				Name: tmpState.Customer.Name,
				Description: 'New customer provisioning request — provisions the customer, users and roles on approval.',
				DocumentDate: (new Date()).toISOString(),
				FormDataJSON: JSON.stringify(this._requestFormData(tmpState)),
				EntityDataJSON: JSON.stringify({ OnEnteringState: { Approved: tmpComprehension } }),
				GUIDDocument: 'NEWCUST-REQ-' + tmpState.Customer.Code + '-' + Date.now(),
				Deleted: 0,
			};
			const tmpCreated = await this.upsertRecord('Document', tmpDoc);
			this.pict.views.HeadlightPanels?.toast({ message: `New customer request for "${tmpState.Customer.Name}" submitted for approval.`, type: 'is-success' });
			if ((typeof window !== 'undefined') && window.location) { window.location.hash = '#/Customers/New'; }
			return tmpCreated;
		}
		catch (pError)
		{
			this.pict.views.HeadlightPanels?.toast({ message: 'Could not submit the request: ' + (pError.message || pError), type: 'is-danger' });
		}
	}

	// --- Push to HeadLight ---

	async pushToHeadLight()
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;

		if (!tmpState.Comprehension1)
		{
			this.buildComprehensions();
		}

		tmpState.PushState.Phase = 'pushing-phase1';
		tmpState.PushState.Progress = 0;
		tmpState.PushState.Log = [];
		tmpState.PushState.Errors = [];
		this.render();

		try
		{
			// Phase 1: Push Customer, Users, Roles
			this.logProgress('Starting Phase 1: Customer setup...');

			// Push Customer
			this.logProgress('Creating Customer record...');
			let tmpCustomerRecord = Object.values(tmpState.Comprehension1.Customer)[0];
			await this.upsertRecord('Customer', tmpCustomerRecord);
			this.updateProgress(10);

			// Push Users (admin + integration)
			this.logProgress('Creating users...');
			let tmpUsers = Object.values(tmpState.Comprehension1.User);
			for (let tmpUserIdx = 0; tmpUserIdx < tmpUsers.length; tmpUserIdx++)
			{
				let tmpUser = tmpUsers[tmpUserIdx];
				let tmpResult = await this.upsertRecord('User', tmpUser);
				if (tmpUser.GUIDUser.includes('integration'))
				{
					tmpState.PushState._integrationUserID = tmpResult?.IDUser;
				}
				if (tmpUser.GUIDUser.includes('admin'))
				{
					tmpState.PushState._adminUserID = tmpResult?.IDUser;
				}
				this.logProgress('  Created user: ' + tmpUser.LoginID + ' (ID: ' + (tmpResult?.IDUser || '?') + ')');
			}
			this.updateProgress(20);

			// Push Roles and capture returned IDs
			this.logProgress('Creating roles...');
			let tmpRoles = Object.values(tmpState.Comprehension1.Role);
			let tmpRoleGUIDToID = {};
			for (let tmpRoleIdx = 0; tmpRoleIdx < tmpRoles.length; tmpRoleIdx++)
			{
				let tmpRoleResult = await this.upsertRecord('Role', tmpRoles[tmpRoleIdx]);
				if (tmpRoleResult && tmpRoleResult.IDRole)
				{
					tmpRoleGUIDToID[tmpRoles[tmpRoleIdx].GUIDRole] = tmpRoleResult.IDRole;
				}
				this.logProgress('  Role: ' + tmpRoles[tmpRoleIdx].Name + ' (ID: ' + (tmpRoleResult?.IDRole || '?') + ')');
			}
			this.updateProgress(40);

			// Role GUIDs are scoped per customer (see buildComprehensions), so resolve the server IDs by the SCOPED GUID.
			const tmpSfx = '-' + String(tmpState.Customer.Code || '').trim();
			let tmpAllEntries = [].concat(RoleCatalog.roles || [], RoleCatalog.permissionGroups || []);
			for (let tmpEntryIdx = 0; tmpEntryIdx < tmpAllEntries.length; tmpEntryIdx++)
			{
				let tmpEntry = tmpAllEntries[tmpEntryIdx];
				let tmpParentGUID = 'PROV-' + tmpEntry.Key + tmpSfx;
				let tmpParentID = tmpRoleGUIDToID[tmpParentGUID];
				if (!tmpParentID || !tmpEntry.ChildRoles) continue;

				for (let tmpChildIdx = 0; tmpChildIdx < tmpEntry.ChildRoles.length; tmpChildIdx++)
				{
					let tmpChildGUID = 'PROV-' + tmpEntry.ChildRoles[tmpChildIdx] + tmpSfx;
					let tmpChildID = tmpRoleGUIDToID[tmpChildGUID];
					if (!tmpChildID) continue;

					await this.upsertRecord('RoleEdge', {
						GUIDRoleEdge: 'RE-' + tmpParentID + '-' + tmpChildID,
						ParentIDRole: tmpParentID,
						ChildIDRole: tmpChildID,
					});
				}
			}
			this.updateProgress(50);

			// Build RolePermissions using server-assigned IDRole values
			this.logProgress('Creating role permissions...');
			for (let tmpPGIdx = 0; tmpPGIdx < (RoleCatalog.permissionGroups || []).length; tmpPGIdx++)
			{
				let tmpPG = RoleCatalog.permissionGroups[tmpPGIdx];
				let tmpPGGUID = 'PROV-' + tmpPG.Key + tmpSfx;
				let tmpPGID = tmpRoleGUIDToID[tmpPGGUID];
				if (!tmpPGID || !tmpPG.PermissionHashes) continue;

				let tmpPermBatch = [];
				for (let tmpScope in tmpPG.PermissionHashes)
				{
					let tmpHashes = tmpPG.PermissionHashes[tmpScope];
					for (let tmpHashIdx = 0; tmpHashIdx < tmpHashes.length; tmpHashIdx++)
					{
						tmpPermBatch.push({
							GUIDRolePermission: 'RP-' + tmpPGID + '-' + tmpHashIdx,
							IDRole: tmpPGID,
							Hash: tmpHashes[tmpHashIdx],
							Allowed: 1,
							Context: tmpScope,
						});
					}
				}
				if (tmpPermBatch.length > 0)
				{
					// Batch in groups of 50
					for (let i = 0; i < tmpPermBatch.length; i += 50)
					{
						await this.upsertRecords('RolePermission', tmpPermBatch.slice(i, i + 50));
					}
					this.logProgress('  ' + tmpPG.Name + ': ' + tmpPermBatch.length + ' permissions');
				}
			}
			this.updateProgress(60);

			// UserRoleJoins — assign admin and integration users to HL-ADMIN role
			this.logProgress('Assigning user roles...');
			let tmpAdminRoleID = tmpRoleGUIDToID['PROV-UM-STD-R-HL-ADMIN' + tmpSfx];
			if (tmpAdminRoleID && tmpState.PushState._adminUserID)
			{
				await this.upsertRecord('UserRoleJoin', {
					GUIDUserRoleJoin: 'PROV-URJ-admin' + tmpSfx,
					IDRole: tmpAdminRoleID,
					IDUser: tmpState.PushState._adminUserID,
				});
			}
			if (tmpAdminRoleID && tmpState.PushState._integrationUserID)
			{
				await this.upsertRecord('UserRoleJoin', {
					GUIDUserRoleJoin: 'PROV-URJ-integration' + tmpSfx,
					IDRole: tmpAdminRoleID,
					IDUser: tmpState.PushState._integrationUserID,
				});
			}
			this.updateProgress(65);

			// Phase 2: Impersonate and push spec data
			tmpState.PushState.Phase = 'impersonating';
			if (tmpState.PushState._integrationUserID)
			{
				this.logProgress('Impersonating integration user...');
				await new Promise((pResolve, pReject) =>
				{
					this.restClient.impersonate(tmpState.PushState._integrationUserID, (pError) =>
					{
						if (pError)
						{
							pReject(pError);
						}
						else
						{
							pResolve();
						}
					});
				});
				this.logProgress('Impersonation successful.');
			}

			tmpState.PushState.Phase = 'pushing-phase2';

			// Push Comprehension 2 entities in dependency order
			if (tmpState.Comprehension2)
			{
				let tmpEntityOrder = [
					'TestSpecificationSet', 'TestSpecification', 'PayItem', 'Organization', 'Lab', 'Test',
					'Material', 'MaterialPayItemJoin', 'ManifestSchema', 'ReportNamedInstance',
					'Project', 'LineItem', 'MaterialLineItemJoin', 'ProjectLineItemMaterialTest',
					'User', 'UserRoleJoin',
				];

				let tmpStep = 0;
				for (let tmpEntityIdx = 0; tmpEntityIdx < tmpEntityOrder.length; tmpEntityIdx++)
				{
					let tmpEntity = tmpEntityOrder[tmpEntityIdx];
					if (!tmpState.Comprehension2[tmpEntity])
					{
						continue;
					}

					let tmpRecords = Array.isArray(tmpState.Comprehension2[tmpEntity])
						? tmpState.Comprehension2[tmpEntity]
						: Object.values(tmpState.Comprehension2[tmpEntity]);

					if (tmpRecords.length === 0)
					{
						continue;
					}

					this.logProgress(`Pushing ${tmpRecords.length} ${tmpEntity} records...`);

					// Batch in groups of 100
					for (let i = 0; i < tmpRecords.length; i += 100)
					{
						let tmpBatch = tmpRecords.slice(i, i + 100);
						await this.upsertRecords(tmpEntity, tmpBatch);
					}

					tmpStep++;
					this.updateProgress(65 + Math.round((tmpStep / tmpEntityOrder.length) * 35));
				}
			}

			tmpState.PushState.Phase = 'complete';
			this.updateProgress(100);
			this.logProgress('Provisioning complete!');
			this.pict.views.HeadlightPanels?.toast({ message: 'Customer provisioned successfully!', type: 'is-success' });
		}
		catch (pError)
		{
			tmpState.PushState.Phase = 'error';
			tmpState.PushState.Errors.push(pError.message || String(pError));
			this.logProgress('Error: ' + (pError.message || String(pError)));
			this.pict.views.HeadlightPanels?.toast({ message: 'Error during provisioning: ' + (pError.message || pError), type: 'is-danger' });
		}
	}

	// --- Helper Methods ---

	upsertRecord(pEntityType, pRecord)
	{
		return new Promise((pResolve, pReject) =>
		{
			this.restClient.upsertEntity(pEntityType, pRecord, (pError, pResult) =>
			{
				if (pError)
				{
					pReject(pError);
				}
				else
				{
					pResolve(pResult);
				}
			});
		});
	}

	upsertRecords(pEntityType, pRecords)
	{
		return new Promise((pResolve, pReject) =>
		{
			this.restClient.upsertEntities(pEntityType, pRecords, (pError, pResult) =>
			{
				if (pError)
				{
					pReject(pError);
				}
				else
				{
					pResolve(pResult);
				}
			});
		});
	}

	logProgress(pMessage)
	{
		let tmpState = this.pict.AppData.CustomerProvisioning;
		let tmpTime = new Date();
		tmpState.PushState.Log.push({
			time: tmpTime.toISOString(),
			message: pMessage,
			Display: tmpTime.toLocaleTimeString() + ' — ' + pMessage,
		});
		tmpState.PushState.StatusText = pMessage;
		this.render();
	}

	updateProgress(pPercent)
	{
		this.pict.AppData.CustomerProvisioning.PushState.Progress = pPercent;
		this.render();
	}
}

module.exports = CustomerProvisioningProvider;

module.exports.default_configuration = {
	ProviderIdentifier: 'CustomerProvisioningProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};
